/**
 * Extracts the milestone value from a celebration item.
 * Supports both legacy string format and new object format.
 * @param {string | object} item - The celebration item.
 * @returns {string | number | null} The milestone value.
 */
const getMilestoneValue = (item) => {
  let value = null;

  if (typeof item === 'string') {
    value = item;
  } else if (typeof item === 'object' && item !== null && 'milestone' in item) {
    value = item.milestone;
  }

  if (
    typeof value === 'string' &&
    (value.startsWith('points__') || value.startsWith('rank_up__'))
  ) {
    return value;
  }

  return null;
};

/**
 * Parses the celebrations array to find and extract the points celebration data.
 * Supports both legacy string format ('points__75000') and new object format with milestone property.
 * @param {Array<string | object>} celebrations - The array of celebration strings or objects.
 * @returns {{points: number} | null} An object containing the extracted points, or null if not found.
 */
export const extractPointsCelebrationData = (celebrations) => {
  if (!Array.isArray(celebrations)) {
    return null;
  }

  const initialResult = { points: 0 };

  const pointsResult = celebrations.reduce((acc, item) => {
    if (acc.points !== 0) {
      return acc;
    }

    const milestone = getMilestoneValue(item);
    if (milestone === null) {
      return acc;
    }

    // Handle legacy string format: 'points__75000'
    if (typeof milestone === 'string' && milestone.startsWith('points__')) {
      const numberString = milestone.split('__')[1];
      const parsedPoints = parseInt(numberString, 10);

      if (!isNaN(parsedPoints) && parsedPoints > 0) {
        return { points: parsedPoints };
      }
    }

    // Handle new format: milestone is already a number
    if (typeof milestone === 'number' && milestone > 0) {
      return { points: milestone };
    }

    return acc;
  }, initialResult);

  if (pointsResult.points === 0) {
    return null;
  }

  return pointsResult;
};

/**
 * Parses the celebrations array to find and extract the rank-up celebration data.
 * Supports both legacy string format ('rank_up__hiker') and new object format with milestone property.
 * @param {Array<string | object>} celebrations - The array of celebration strings or objects.
 * @param {string} userFirstName - The first name of the user.
 * @returns {{rank: string, name: string} | null} The rank celebration data or null if not found.
 */
export const extractRankUpCelebrationData = (celebrations, userFirstName) => {
  if (!Array.isArray(celebrations)) {
    return null;
  }

  for (const item of celebrations) {
    const milestone = getMilestoneValue(item);
    if (milestone === null || typeof milestone !== 'string') {
      continue;
    }

    // Handle legacy format: 'rank_up__hiker'
    if (milestone.startsWith("rank_up__")) {
      return {
        rank: milestone,
        name: userFirstName,
      };
    }

    // Handle new format: milestone is just the rank name (e.g., 'hiker')
    // Only treat as rank name if it doesn't contain '__' (to avoid matching other celebration types)
    if (milestone.length > 0 && !milestone.includes('__')) {
      return {
        rank: `rank_up__${milestone}`,
        name: userFirstName,
      };
    }
  }

  return null;
};

/**
 * Returns the array of modals to be set as modal pages.
 * @param {Array<any>} modals - The array of modal data.
 * @returns {Array<any> | null} The modals array.
 */
export const getModalPages = (modals) => {
  if (Array.isArray(modals) && modals.length > 0) {
    return modals;
  }
  return null;
};
